import CashCheckInbox from '@/dashboard/money/CashCheckInbox';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { TodayAppointment, TodayAppointmentsData } from '@/dashboard/types';
import {
  fetchDoctorConsultContext,
  fetchDoctorCaseHistory,
  fetchTodayAppointments,
  fetchTreatmentCourseJourney,
} from '@/dashboard/api';
import DashboardLoadError from '@/dashboard/DashboardLoadError';
import {
  dayBoardNewReturningBadge,
  dayBoardPatientLabel,
  dayBoardUmr,
  dayBoardWaitingMinutes,
  dayStatusChip,
} from '@/dashboard/appointments/ClinicDayBoard';
import { useClinicLens } from '@/dashboard/clinic-context';
import { useDashboardMeContext } from '@/dashboard/me-context';
import DoctorInboxRail from '@/dashboard/doctor/DoctorInboxRail';
import DoctorQueueContextDrawer from '@/dashboard/doctor/DoctorQueueContextDrawer';
import GlobalPatientSearchModal from '@/dashboard/patients/GlobalPatientSearchModal';
import RoleHomeSearchPortal from '@/dashboard/ui/RoleHomeSearchPortal';
import {
  AcBadge,
  AcButton,
  ClinicScopeLabel,
  DataTable,
  DateCommandBar,
  DaySummaryRibbon,
  DOCTOR_CASE_SHEET_OWED_LABEL,
  doctorVisitStateChip,
  OpeningLine,
  PageHeader,
  SegmentedControl,
  useDayScope,
  type AcBadgeTone,
  type DataTableColumn,
} from '@/dashboard/ui';
import {
  ageSexLabel,
  boardDoctorSegment,
  chairChipLabel,
  consultDoorForRow,
  consultPathForAppointment,
  consultRowHint,
  dayProgressLine,
  deriveDoctorDayModel,
  doctorOpeningClauses,
  doctorOpeningState,
  doctorQueueBandGroups,
  doctorStartableWaitingRows,
  doctorQueueOrder,
  doctorMyPatientRows,
  doctorScopedBoardRows,
  doctorQueueTransportAvailable,
  readDoctorQueueClinicView,
  MID_DOT,
  myDayStats,
  queueRegistrationChip,
  queueTailLabel,
  stateWithWaitLabel,
  identityCountValue,
  priorVisitCountsUnavailable,
  PRIOR_VISIT_COUNTS_SEAM,
  doctorClockLabel,
  doctorPlaceClockLine,
  waitProvenanceLine,
  chairEmptyLine,
  nextCardFacts,
  type DoctorNextCardFact,
} from '@/dashboard/doctor/doctor-home-view';
import { consultNoteSections } from '@/dashboard/doctor/doctor-consult-view';
import type { OwedWorkSummary } from '@/dashboard/doctor/clinical-evidence-view';
import { nameInitials } from '@/dashboard/doctor-v2/cockpit-view';
import { formatDurationHm, formatIstTime, formatIstShortDate } from '@/lib/datetime-ist';
import { paidAcrossVisitsMinorUnits } from '@/dashboard/doctor/doctor-consult-view';
import '@/dashboard/doctor/doctor-home-parity.css';

// Re-export: the Q4 filter stays pinned where it always was
// (clinic-day-board.test imports it from this module).
export { doctorQueueRows } from '@/dashboard/doctor/doctor-home-view';

/**
 * DOCTOR-COCKPIT-VISUAL-CONFORM-V1 -- the doctor's day as the approved
 * Candidate B "Cockpit" QUEUE anatomy (CANDIDATE-B-PIXELS-WIN; the frozen
 * reference docs/design-studio/doctor-os/candidate-b/doctors-day.html):
 *
 *   1. HEADER -- "My day" + the ONE registered DateCommandBar (live via the
 *      existing day-scope primitive + the board read's existing ?date seam;
 *      DAY-SCOPE-PRIMITIVE-AND-ADOPTION-V1) + the metrics as a QUIET TEXT
 *      RIBBON (DENSITY rule 3: numbers ride as a ribbon, never a second
 *      dashboard -- the KPI tile row is retired from this surface).
 *   2. TWO-UP HEROES -- "In the chair now" (the in-consult patient, Resume)
 *      beside "Next in" (first present by arrival, Start) as co-equal
 *      command panels. TODAY only (a planning day has no chair truth --
 *      contextual existence).
 *   3. WORKLIST -- "The rest of the day": a compact table (time / patient /
 *      state / action) grouped Present then Expected, with the filter-tab
 *      row (All / Present / Expected).
 *   4. DONE BAR -- one foot bar: outcome chips + names + a Show disclosure.
 *
 * BEHAVIOR UNCHANGED UNDERNEATH (REFERENCE-OVER-LEGACY: candidate-b owns
 * the pixels; the engines stay): the SAME fenced day read (view=board,
 * DAY_BOARD_ROLES), the SAME derivations (doctorQueueOrder /
 * doctorQueueBandGroups / myDayStats / consultDoorForRow /
 * queueRegistrationChip / dayStatusChip / dayBoardPatientLabel), the SAME
 * consult doors and navigation, and the SAME doctor-safe context drawer on
 * a name click (rowOpenKind law: the doctor opens DoctorQueueContextDrawer,
 * never the lead workspace drawer). The doctor stays a non-action role on
 * the day board (DAY_BOARD_ACTION_ROLES untouched) -- this surface renders
 * rows and doors, it performs no desk write.
 *
 * DOCTOR-HOME-STATE-MODEL-V1 (founder walk verdicts F1-F6, 2026-08-14): the
 * page derives EVERY state fact from THE ONE owner
 * (@/dashboard/ui/doctor-visit-state) -- header counts (now five, Cancelled
 * added), the chair (exactly one), row chips, and band membership -- so one
 * visit renders one state everywhere. The "All patients" header door and the
 * doctor "Patients" nav tab are REMOVED (F3/F4, knowingly reversing the
 * July-20 tab ruling); any-patient reach is the doctor-fenced search
 * (Mobile / UMR / name, the front-desk posture) -- SHIPPED-WORK-TRUTH-
 * CORRECTIVE-V1 (T2, founder ruling 2026-08-15) moved that door to the TOP
 * of this page as the front-desk-placed global search bar opening THE ONE
 * GlobalPatientSearchModal (doctor context), replacing the F5 bottom panel.
 *
 * Carried laws from the prior composition (unchanged): NO-MANUAL-ID (no
 * appointment-id entry anywhere), LES-016 (no dead doors), DR-20/DR-21 (the
 * evidence inbox card stays config-gated; the recent-patients panel stays
 * retired), B2-v2 (no incentive/target/monetary-performance field renders
 * here), FOLLOW-UPS-DUE stays absent (no composable read).
 *
 * The old header-date dashboard read is RETIRED WITH ITS RENDER (the DR-21
 * "no read nobody renders" law): the date axis is now the DateCommandBar
 * over day-scope, so fetchDoctorDashboard -- whose only rendered fact here
 * was the header date -- no longer fires from this page.
 */

/** Owed rows shown on the DAY BOARD before the "View all" fold (founder
 *  ruling 2026-09-01). Narrower than the rail's own DR-20 cap on purpose --
 *  see the mount site and DoctorInboxRailProps. */
const DAY_BOARD_OWED_ROW_CAP = 3;

type WorklistTab = 'all' | 'present' | 'expected';

type QueueState =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      rows: TodayAppointment[];
      /** DOCTOR-RIBBON-FALSE-ZERO-V1: the day-level prior-visit verdict the
       *  wire carries (optional -- an older backend omits it = available). */
      priorVisitAvailability?: TodayAppointmentsData['priorVisitAvailability'];
    }
  | { kind: 'error' };

/** The drawer target (appointment id + the honest seed title). */
interface DrawerTarget {
  readonly appointmentId: number;
  readonly seedTitle: string;
}

/** THE CHAIR ENRICHMENT (coverage rows D19 + D21 -- the founder TASK/PENDING
 *  detail): for the ONE in-the-chair row, the hero composes two EXISTING
 *  doctor-fenced endpoints -- the consult context (owed case-sheet sections)
 *  and, when a course exists, the course journey (course name + visit
 *  ordinal). Display-only, best-effort: any failure renders ABSENCE, never a
 *  fabricated line. No new endpoint, zero writes. */
interface ChairEnrichment {
  readonly appointmentId: number;
  /** e.g. "Diagnosis + Clinical Notes owed to finish"; null = nothing owed
   *  or not derivable. */
  readonly owedLine: string | null;
  /** e.g. "Gum treatment (Periodontics) - visit 3"; null = no course. */
  readonly courseLine: string | null;
}

/**
 * DOCTOR-DAY-VISIT-HISTORY-BADGE-V1 -- THE ONE doctor-side render of the
 * new and returning badge (tenant report: a Partha dentist saw this capability
 * "removed" because it only ever reached one card, on one day, in one
 * state). Every doctor render site -- both hero panels and every worklist
 * row (Present, Expected, Draft open, Done today) -- calls this SAME
 * expression, so the badge never grows a second copy. Wraps THE ONE owner
 * derivation (dayBoardNewReturningBadge, ClinicDayBoard.tsx) unchanged; an
 * unlinked or unproven row renders NOTHING here too (never a guessed
 * badge). Extends the existing doctor-chair-visit-badge test-id family to
 * every site -- no second doctor-side name is minted.
 */
export function doctorVisitHistoryBadge(row: TodayAppointment): ReactNode {
  const badge = dayBoardNewReturningBadge(row);
  return badge === null ? null : (
    <AcBadge tone={badge.tone} testId={`doctor-chair-visit-badge-${row.id}`}>
      {badge.label}
    </AcBadge>
  );
}

export default function DoctorPortalPage() {
  const [queue, setQueue] = useState<QueueState>({ kind: 'loading' });
  const [tab, setTab] = useState<WorklistTab>('all');
  const [drawer, setDrawer] = useState<DrawerTarget | null>(null);
  // THE OWED SUMMARY, published by the ONE rail that already reads it (never a
  // second fetch of the same endpoint). Null until that read lands, which is
  // exactly when the opening line must not claim anything about it.
  const [owedSummary, setOwedSummary] = useState<OwedWorkSummary | null>(null);
  const navigate = useNavigate();

  // THE DAY AXIS (candidate-b day-nav): the registered day-scope primitive
  // binds the selected clinic day to the URL; the board read moves days via
  // its EXISTING ?date seam (the same fenced read -- no new endpoint).
  const day = useDayScope();
  const isToday = day.date === day.today;

  // The day-board read. A multi-clinic doctor's queue honors the GLOBAL
  // clinic picker (narrowing-within-verified-scope); with no valid pick the
  // server resolves the clinic from session scope as before.
  const meCtx = useDashboardMeContext();
  const clinicCtx = useClinicLens();
  // THE LENS OWNS "WHERE AM I" (119-CLINIC-OPERATING-SPINE-V1, Phase 2).
  // ONE truth for this page: the same value drives the READ below and the
  // clinic named in the header. The old gate re-validated the pick against
  // `me.clinicIds` (the REACH list, empty for an owner) while the HEADING read
  // the lens ungated -- so the two could disagree and the page named one clinic
  // while showing another's day. A label that lies about its data is this
  // vertical's thesis inverted; there is now nothing to disagree with.
  const globalPick = clinicCtx?.selectedClinicId ?? null;
  const loadQueue = useCallback(() => {
    fetchTodayAppointments(
      globalPick ?? undefined,
      'board',
      // Today = the EXISTING realtime call, byte-identical; another day
      // rides the read's own planning-date seam (APPOINTMENT-BOOK-V1 S1).
      isToday ? undefined : day.date,
    )
      .then((data) =>
        setQueue({
          kind: 'ready',
          rows: doctorScopedBoardRows(data),
          priorVisitAvailability: data.priorVisitAvailability,
        }),
      )
      .catch(() => setQueue({ kind: 'error' }));
  }, [globalPick, isToday, day.date]);
  useEffect(() => {
    setQueue({ kind: 'loading' });
    loadQueue();
  }, [loadQueue]);

  // The #294 lens signal now gates the TOP SEARCH DOOR (T2, founder ruling
  // 2026-08-15, superseding the F5 bottom-panel mount: the doctor's search
  // is the SAME door and the SAME placement as the front desk's -- the
  // global search bar at the top of Home opening THE ONE
  // GlobalPatientSearchModal in its doctor context; the "All patients"
  // header door and the Patients nav tab stay REMOVED per F3/F4). A doctor
  // with no clinic sees their day and no search -- honest absence, the same
  // lawful-signal rule as before (LES-016: no dead doors).
  const lensEnabled = meCtx?.me?.doctorLensEnabled === true;
  // T2: the hero search box's typed query + the centered modal it opens --
  // the SAME state shape the front-desk Home carries (WalkinApp FO-3).
  const [heroQuery, setHeroQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  // CORRECTIVE ROUND 1 (product-level AC-3): the owed-work rail no longer
  // gates on me.clinicalEvidenceEnabled -- it mounts unconditionally and the
  // READ (config-aware server-side) decides what is owed. The /me signal is
  // still emitted for the CONSULT evidence-capture surfaces (ConsultWorkspace
  // / ClinicalEvidencePanel), which continue to gate on it, unchanged.

  // P2 owns the additive DashboardMe declaration and /me projection. Read the
  // agreed wire defensively without asserting a field into the shared type.
  const me = meCtx?.me;
  // The separately reviewed transport may not be deployed yet. Preserve the
  // working clinic board until it exists; never offer a personal-view control
  // that the current server cannot supply.
  const identityPayload = me && 'doctorQueueIdentity' in me ? me.doctorQueueIdentity : undefined;
  const doctorQueueEnabled = me?.role === 'doctor' && doctorQueueTransportAvailable(identityPayload);
  const doctorUsesClinicBoard = me?.role === 'doctor' && !doctorQueueEnabled;
  const identity = readDoctorQueueClinicView(
    identityPayload, globalPick,
  );
  const [queueView, setQueueView] = useState<{ clinicId: number | null; value: 'my' | 'clinic' }>({ clinicId: null, value: 'my' });
  const selectedView = queueView.clinicId === globalPick ? queueView.value : 'my';
  const clinicRows = queue.kind === 'ready' ? queue.rows : [];
  const ownRows = doctorMyPatientRows(clinicRows, globalPick ?? 0, identity.status === 'available' ? identity.doctorId : null);
  const identityUnavailable = doctorQueueEnabled && selectedView === 'my' && ownRows === null;
  const rows = !doctorQueueEnabled ? clinicRows : selectedView === 'my' ? (ownRows ?? [])
    : clinicRows.filter(row => row.clinicId === globalPick);
  /** The day read has landed. Off this, every ribbon fact prints the honest
   *  dash rather than a fabricated zero (DENSITY rule 6). */
  const ready = queue.kind === 'ready' && !identityUnavailable;
  // F1 (DOCTOR-HOME-STATE-MODEL-V1): THE ONE state derivation -- the header
  // counts, the chair, every row chip, and the band partition all read this
  // single model (one computation, one truth; the contradiction row is
  // structurally impossible because chip and door read the same owner).
  const model = useMemo(() => deriveDoctorDayModel(rows), [rows]);
  const stats = myDayStats(rows, model);
  /** DOCTOR-RIBBON-FALSE-ZERO-V1 (L-16): did the server's prior-visit
   *  derivation ANSWER for this day? Read from the wire's explicit signal and
   *  nothing else -- never inferred from the counts, the nulls or the rows.
   *  When it did not, the two identity cells print the honest dash and the
   *  ribbon says why once, in its own seam slot. */
  const priorVisitUnavailable =
    queue.kind === 'ready' && priorVisitCountsUnavailable(queue.priorVisitAvailability);
  // The masthead story: the day's shape, then what is still owed. Every clause
  // maps 1:1 to a figure rendered below it (S1 contract); a figure that has
  // not loaded contributes no clause rather than a zero.
  const progressLine = dayProgressLine(rows, stats, ready);
  const openingClauses = doctorOpeningClauses(progressLine, owedSummary);
  // One wait-clock reading per render (the same nowMs every row reads).
  const nowMs = Date.now();
  // QUEUE-ORDER LAW: actionability bands up top; the day's finished rows
  // collapse into the done bar (count always visible). Pure derivation.
  const { active, done } = doctorQueueOrder(rows, model);
  const bands = useMemo(() => doctorQueueBandGroups(active, model), [active, model]);
  const inConsultBand = bands.find((b) => b.key === 'in_consult');
  const presentBand = bands.find((b) => b.key === 'present');
  const draftOpenBand = bands.find((b) => b.key === 'draft_open');
  const expectedBand = bands.find((b) => b.key === 'expected');

  // THE TWO HEROES (today only): the chair (the owner-arbitrated ONE
  // in-consult row) and the next patient -- the first WAITING person by
  // arrival (the band's own order). A present person whose consult record
  // is already open (draft_open) is OWED PAPERWORK, not "Next in": their
  // row rides the worklist wearing the owed chip instead of commanding the
  // hero (F1: one visit, one state).
  const heroNow = isToday ? (inConsultBand?.rows[0] ?? null) : null;
  const startableWaiting = doctorStartableWaitingRows(presentBand?.rows ?? [], model);
  const heroNext = isToday ? (startableWaiting[0] ?? null) : null;
  const heroIds = new Set(
    [heroNow?.id, heroNext?.id].filter((id): id is number => id != null),
  );

  // THE CHAIR ENRICHMENT (D19/D21): compose the chair row's owed-work +
  // course context over the two EXISTING doctor endpoints. Best-effort;
  // failure = absence.
  const [chair, setChair] = useState<ChairEnrichment | null>(null);
  const chairId = heroNow?.id ?? null;
  useEffect(() => {
    if (chairId === null) {
      setChair(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const ctx = await fetchDoctorConsultContext(chairId);
        const sheet = ctx.caseSheet;
        let owedLine: string | null = null;
        if (sheet !== null && sheet.status === 'draft') {
          const owed = consultNoteSections(ctx)
            .filter((s) => {
              const v = sheet[s.field];
              return v == null || String(v).trim() === '';
            })
            .map((s) => s.label);
          owedLine =
            owed.length > 0
              ? `${owed.slice(0, 2).join(' + ')}${owed.length > 2 ? ` + ${owed.length - 2} more` : ''} owed to finish`
              : null;
        }
        let courseLine: string | null = null;
        const courseId = sheet?.treatmentCourseId ?? null;
        if (courseId !== null) {
          try {
            const journey = await fetchTreatmentCourseJourney(courseId);
            const ordered = [...journey.visits].sort((a, b) =>
              (a.appointmentTimeIso ?? '') < (b.appointmentTimeIso ?? '') ? -1 : 1,
            );
            const ordinal =
              ordered.findIndex((v) => v.appointmentId === chairId) + 1;
            if (journey.courseName !== null) {
              // ROUND-3 COPY DIFF: the mockup's course grammar --
              // "Gum treatment, visit 3 of 4" (live ordinal + visitCount).
              courseLine =
                ordinal > 0
                  ? `${journey.courseName}, visit ${ordinal} of ${journey.visitCount}`
                  : journey.courseName;
            }
          } catch {
            /* course line stays absent */
          }
        }
        if (!cancelled) setChair({ appointmentId: chairId, owedLine, courseLine });
      } catch {
        if (!cancelled) setChair(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [chairId]);

  // A20/A21 (DOCTOR-HOME-PARITY-V1) -- THE NEXT CARD'S FOUR LABELLED FACTS.
  //
  // The approved frame's next-patient card carries Allergy / Reports / Last
  // visit / Ledger (home-normal.html:204-207), with the allergy row carrying
  // a STATE: calm "None - 14 Jul" or red "ALLERGY ALERT" + "Meds: 3"
  // (home-dense-day.html:214).
  //
  // REUSED, NOT BUILT: this is the SAME shape as the chair enrichment above --
  // the same `fetchDoctorConsultContext` read, for the ONE hero row, chained
  // with `fetchDoctorCaseHistory` exactly as the consult page composes its own
  // ledger through `paidAcrossVisitsMinorUnits`. No new endpoint, no new wire
  // field, no schema. The clinical profile it reads is the DOCTOR-ONLY wire
  // that already rides that read, and the doctor is the clinical audience.
  // Best-effort: any failure is ABSENCE, never a guess.
  const [nextFacts, setNextFacts] = useState<{
    readonly appointmentId: number;
    readonly facts: readonly DoctorNextCardFact[];
  } | null>(null);
  const nextId = heroNext?.id ?? null;
  useEffect(() => {
    if (nextId === null) {
      setNextFacts(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const ctx = await fetchDoctorConsultContext(nextId);
        let lastVisitSummary: string | null = null;
        let paid: number | null = null;
        let visitCount: number | null = null;
        const patientId = ctx.patient.patientId;
        if (patientId !== null) {
          try {
            const history = await fetchDoctorCaseHistory(patientId);
            const earlier = history.entries.filter(
              (e) => e.appointmentId !== nextId,
            );
            // "14 Jul 2026 - Consultation" (home-normal.html:206): the
            // visit's DATE and what it was about, from the entry's own
            // served fields -- the same two facts the consult history rail
            // already renders. Either half may be absent; the line degrades
            // rather than inventing the missing one.
            const last = earlier[0] ?? null;
            if (last != null) {
              const when = formatIstShortDate(last.createdAt);
              const what = (last.chiefComplaintCategory ?? last.chiefComplaint ?? '').trim();
              lastVisitSummary =
                when === ''
                  ? what === ''
                    ? null
                    : what
                  : what === ''
                    ? when
                    : `${when} ${MID_DOT} ${what}`;
            }
            visitCount = earlier.length > 0 ? earlier.length : null;
            paid = paidAcrossVisitsMinorUnits(
              { kind: 'ready', history },
              nextId,
              ctx.paymentsForVisit,
            );
          } catch {
            /* history absent -> those two facts simply drop out */
          }
        }
        const facts = nextCardFacts({
          profile: ctx.clinicalProfile ?? null,
          reports: ctx.caseSheet?.chiefComplaint ?? null,
          lastVisitSummary,
          paidAllVisitsMinorUnits: paid,
          visitCount,
        });
        if (!cancelled) setNextFacts({ appointmentId: nextId, facts });
      } catch {
        if (!cancelled) setNextFacts(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nextId]);

  // The worklist: every active row not already commanding a hero panel,
  // grouped Present then Expected (the candidate-b band grammar).
  const restPresent = (presentBand?.rows ?? []).filter((r) => !heroIds.has(r.id));
  const restInConsult = (inConsultBand?.rows ?? []).filter(
    (r) => !heroIds.has(r.id),
  );
  // W-5 (corrective): the desk-closed open drafts render their OWN group
  // between Present and Expected -- never mixed with not-arrived rows.
  const restDraftOpen = draftOpenBand?.rows ?? [];
  const restExpected = expectedBand?.rows ?? [];
  const presentGroup = [...restInConsult, ...restPresent];
  const restCount =
    presentGroup.length + restDraftOpen.length + restExpected.length;


  // Done-bar derivations: outcome chips + the names line (honest counts
  // from the same rows the collapsed board renders). ROUND-3 DNA: the
  // mockup groups the names -- completed list, mid-dot, then the
  // no-show/cancelled list.
  const doneNoShow = done.filter((r) => r.status === 'no_show');
  const doneCancelled = done.filter((r) => r.status === 'cancelled');
  const doneComplete = done.filter(
    (r) => r.status !== 'no_show' && r.status !== 'cancelled',
  );
  const doneNames = [
    doneComplete.map((r) => dayBoardPatientLabel(r)).join(', '),
    [...doneNoShow, ...doneCancelled]
      .map((r) => dayBoardPatientLabel(r))
      .join(', '),
  ]
    .filter((part) => part !== '')
    .join(` ${MID_DOT} `);
  // ROUND-3 (mockup header): "<clinic> <mid-dot> <doctor>" -- the doctor
  // segment names the board's own served roster name. DOCTOR-CONSULT-
  // CORRECTIVE-V1 (BUILD 2, L-06): the old form took the FIRST non-null
  // assigned name across ALL rows, which is the day's owner only while the
  // board carries ONE doctor. With several it named one doctor over a board
  // containing several -- a possessive scope the surface does not have.
  // THE ONE derivation now decides (truthful-labelling law); the wire, the
  // scope filter and the row set are untouched.
  const boardDoctor = boardDoctorSegment(rows);

  // THE TAB FILTER -- three visible controls over the SAME rows (a lens, never
  // a second read). Restored with the tabs themselves.
  const filteredPresent = tab === 'expected' ? [] : presentGroup;
  const filteredDraftOpen = tab === 'all' ? restDraftOpen : [];
  const filteredExpected = tab === 'present' ? [] : restExpected;

  // The consult door for one queue row (shared by the heroes and the
  // worklist -- one door law, several instances of the one derivation).
  const doctorConsultDoor = (
    row: TodayAppointment,
    emphasis: 'hero' | 'row' = 'row',
    withReadinessHint = true,
  ): ReactNode => {
    const door = consultDoorForRow(row);
    if (door === null) {
      // No door yet: say WHY (never a blank cell) -- a booked row is
      // waiting for the front desk to check the patient in.
      const hint = consultRowHint(row);
      return hint === null ? null : (
        <span
          className="day-board__row-hint doctor-day__seam"
          data-testid={`doctor-consult-hint-${row.id}`}
        >
          {hint}
        </span>
      );
    }
    // THE CONVERSION CHIP (A3/LEAD-UNTIL-CONSULT): a registered lead
    // awaiting first consult says so beside its Start door -- the doctor
    // sees the conversion moment coming, never a surprise mint. ROUND-3:
    // worklist rows render the SAME string in the mockup's under-name sub
    // position instead (SAY IT ONCE -- the caller opts this slot out).
    const conversionChip = withReadinessHint ? queueRegistrationChip(row) : null;
    return (
      <span className="doctor-portal__door-cell">
        {conversionChip !== null ? (
          <span
            className="day-board__row-hint"
            data-testid={`doctor-conversion-chip-${row.id}`}
          >
            {conversionChip}
          </span>
        ) : null}
        <AcButton
          size="sm"
          variant={
            emphasis === 'hero' && row.id === (heroNow?.id ?? heroNext?.id)
              ? 'primary'
              : 'secondary'
          }
          onClick={() => navigate(consultPathForAppointment(row.id))}
          data-testid={`doctor-consult-door-${row.id}`}
        >
          {door.label}
        </AcButton>
      </span>
    );
  };

  /** One compact worklist row: time / patient (name opens the doctor-safe
   *  context drawer) / state chip / action. ROUND-3 DNA: the mockup's row
   *  grammar -- the readiness line rides UNDER the name (plain small), the
   *  state cell reads "<state> <mid-dot> <duration>", Booked/Confirmed wear
   *  the reference's info tone (DNA finding F3 notes the canon delta).
   *
   *  F1 STATE LAW: the chip renders THE OWNER's state -- the same derivation
   *  the row's door reads -- so "Visit complete" beside "Resume consult" is
   *  impossible by construction. Only the EXPECTED state keeps the stored-
   *  status word (Booked / Confirmed / ... -- not-here-yet is the one band
   *  where the desk word IS the doctor's truth). */
  /** THE WORKLIST CELLS. CROSS-ROLE-HOME-GRAMMAR-V1 (founder ruling D9):
   *  the columns are declared ONCE, on the registered table primitive, and
   *  every group renders INSIDE that one table -- so Time, Patient, State and
   *  the door line up down the WHOLE screen instead of each band re-declaring
   *  its own widths. That is the change that makes this surface and the front
   *  desk board read as the same object. The cell CONTENT is unchanged: same
   *  derivations, same chips, same doors, same testids.
   *
   *  ROUND-3 DNA (carried): the readiness line rides UNDER the name (plain
   *  small), the state cell reads "<state> <mid-dot> <duration>", and
   *  Booked/Confirmed wear the reference's info tone (DNA finding F3 notes
   *  the canon delta).
   *
   *  F1 STATE LAW: the chip renders THE OWNER's state -- the same derivation
   *  the row's door reads -- so "Visit complete" beside "Resume consult" is
   *  impossible by construction. Only the EXPECTED state keeps the stored-
   *  status word (Booked / Confirmed / ... -- not-here-yet is the one band
   *  where the desk word IS the doctor's truth). */
  const rowChip = (
    row: TodayAppointment,
  ): { label: string; tone: AcBadgeTone } => {
    const state = model.stateById.get(row.id);
    const ownerChip = state !== undefined ? doctorVisitStateChip(state) : null;
    const storedChip = dayStatusChip(row.status);
    return ownerChip !== null
      ? { label: ownerChip.label, tone: ownerChip.tone }
      : {
          label: storedChip.label,
          tone:
            row.status === 'booked' || row.status === 'confirmed'
              ? ('info' as const)
              : storedChip.tone,
        };
  };

  const worklistColumns: ReadonlyArray<DataTableColumn<TodayAppointment>> = [
    {
      key: 'time',
      header: 'Time',
      render: (row) => (
        <span className="doctor-day__row-time">
          {/* D38: a slotless row that is PRESENT reads "now" (honest -- the
              patient is here with no slot, the walk-in case); a slotless
              expected row keeps the unknown mark. */}
          {formatIstTime(row.appointmentTime) ||
            (row.status === 'arrived' || row.status === 'checked_in' ? 'now' : '--:--')}
        </span>
      ),
    },
    {
      key: 'patient',
      header: 'Patient',
      render: (row) => {
        const name = dayBoardPatientLabel(row);
        const umr = dayBoardUmr(row);
        const readiness = queueRegistrationChip(row);
        return (
          <span className="doctor-day__row-who">
            <span className="doctor-day__avatar doctor-day__avatar--row" aria-hidden="true">
              {nameInitials(name)}
            </span>
            <span className="doctor-day__row-id">
              {/* DOCTOR-CONTEXT LAW: the name click opens the doctor-safe
                  context drawer keyed on the APPOINTMENT id -- never the lead
                  workspace drawer (rowOpenKind, ClinicDayBoard). Rendered
                  through the ONE AcButton owner (design-system RULE 3) with
                  the row-name skin. */}
              <AcButton
                variant="ghost"
                size="sm"
                className="doctor-day__row-name"
                onClick={() => setDrawer({ appointmentId: row.id, seedTitle: name })}
                data-testid={`doctor-day-open-${row.id}`}
              >
                {name}
              </AcButton>
              {umr !== null ? (
                <span className="doctor-day__row-umr">{umr}</span>
              ) : readiness !== null ? (
                // The W-20 ruled readiness string, in the mockup's under-name
                // plain-sub position (SAY IT ONCE: the door cell no longer
                // repeats it on worklist rows).
                <span
                  className="doctor-day__row-plain"
                  data-testid={`doctor-conversion-chip-${row.id}`}
                >
                  {readiness}
                </span>
              ) : null}
              {/* DOCTOR-DAY-VISIT-HISTORY-BADGE-V1: every ordinary row now
                  carries the same new and returning badge the front-desk board
                  and the chair hero already show -- beside identity, never
                  inside the row-state cell, so the state cell's two-chip cap
                  (docs/design/design-language/07-BEHAVIOURAL-LAWS.md) is
                  never spent by it. */}
              {doctorVisitHistoryBadge(row)}
            </span>
          </span>
        );
      },
    },
    {
      key: 'state',
      header: 'State',
      render: (row) => {
        const state = model.stateById.get(row.id);
        const chip = rowChip(row);
        // The wait clock rides ONLY the queue-waiting states (a finalized row
        // never wears a wait clock again -- the R-1 ungranted close renders
        // "Visit finalized", not "Checked in <mid-dot> 5h").
        const waitClock =
          state === 'waiting' || state === 'draft_open'
            ? dayBoardWaitingMinutes(row, nowMs)
            : null;
        // A12 IS WITHDRAWN. This package shipped a wait escalation on 20/45
        // minute thresholds that were builder choices; the approved frames
        // show example waits, not a rule, and the trace found no approved rule
        // or configuration for patient-wait escalation anywhere (the reasoning
        // and the five lead-side SLA classes it ruled out are recorded in
        // doctor-home-view.ts). The wait therefore renders EXACTLY as main
        // renders it -- the row's own state tone -- and the gap is recorded
        // rather than a new rule kept because it was labelled unsourced.
        //
        // A13 (kept): the wait's PROVENANCE, so the clock is never a bare
        // number. The visible line is the approved frame's SHORT grammar
        // ("Slot 09:10 - checked in 09:00", home-dense-day.html:151), which is
        // what fits a state cell; the full sentence, which the frame prints as
        // a FULL-WIDTH detail row under the queue row (:158), rides the title.
        const provenance = waitClock !== null ? waitProvenanceLine(row) : null;
        return (
          <span className="doctor-day__row-state">
            {waitClock !== null ? (
              // The waiting row's ONE state cell: the owner's state word + the
              // SHARED wait-minutes derivation through THE ONE duration
              // grammar (one clock, one grammar; this is only its skin).
              <AcBadge tone={chip.tone} testId={`doctor-day-wait-${row.id}`}>
                {stateWithWaitLabel(chip.label, waitClock)}
              </AcBadge>
            ) : (
              <AcBadge tone={chip.tone}>{chip.label}</AcBadge>
            )}
            {provenance !== null ? (
              <span
                className="doctor-day__row-provenance"
                data-testid={`doctor-day-wait-provenance-${row.id}`}
                title={provenance.sentence}
              >
                {provenance.short}
              </span>
            ) : null}
            {/* D52 (founder TASK/PENDING detail) restated by F1: a row in the
                draft_open state SAYS WHAT IS OWED (the case sheet) beside its
                state chip; the row's door is the Resume act. Two-chip cap
                respected; the owed vocabulary is the owner's ONE constant
                (shared with the owed-work inbox). */}
            {state === 'draft_open' ? (
              <AcBadge tone="pending" testId={`doctor-day-draft-${row.id}`}>
                {DOCTOR_CASE_SHEET_OWED_LABEL}
              </AcBadge>
            ) : null}
          </span>
        );
      },
    },
    {
      key: 'act',
      header: 'Actions',
      align: 'end',
      render: (row) => (
        <span className="doctor-day__row-act">
          {doctorConsultDoor(row, 'row', queueRegistrationChip(row) === null)}
        </span>
      ),
    },
  ];

  /** One hero command panel (candidate-b two-up heroes; ROUND-3 DNA: the
   *  mockup's one-line sub grammar with mid-dot joins, the chair clock chip
   *  and the served demographic segment). */
  const heroPanel = (
    row: TodayAppointment,
    kind: 'now' | 'next',
  ): ReactNode => {
    const name = dayBoardPatientLabel(row);
    const umr = dayBoardUmr(row);
    const waiting = dayBoardWaitingMinutes(row, nowMs);
    // The mockup sub-line segments after the mono UMR (each a served fact;
    // absent facts simply drop out): NOW = age/sex + course context;
    // NEXT = age/sex + the checked-in line (or the slot time).
    const demo = ageSexLabel(row.ageYears, row.gender);
    const subSegments: string[] = [];
    if (demo !== null) subSegments.push(demo);
    if (kind === 'now') {
      if (chair?.appointmentId === row.id && chair.courseLine !== null) {
        subSegments.push(chair.courseLine);
      }
    } else if (row.status === 'checked_in' || row.status === 'arrived') {
      // F1 Finding B (state-split fix): the word matches the STORED status
      // -- "Checked in" only for a checked-in row, "Arrived" for an arrived
      // one -- and the clock is the moment the row ENTERED that status
      // (currentStatusSince), falling back to the arrival stamp. The old
      // line printed "Checked in <arrival time>" off the arrival EVENT
      // alone, on any row, contradicting the desk board's stored state.
      const word = row.status === 'checked_in' ? 'Checked in' : 'Arrived';
      const at = row.currentStatusSince ?? row.arrivedAt ?? null;
      subSegments.push(
        `${word} ${(at !== null && formatIstTime(at)) || '--:--'} ${MID_DOT} first by arrival`,
      );
    } else {
      subSegments.push(formatIstTime(row.appointmentTime) || '--:--');
    }
    // F1: the chair chip -- "In consult <mid-dot> 9m" from the recorded
    // consult_started stamp through THE ONE duration grammar; "In consult"
    // alone without a usable stamp (never a fabricated clock).
    const chairChip = kind === 'now' ? chairChipLabel(row, nowMs) : null;
    return (
      <section
        className={`doctor-day__hero${kind === 'now' ? ' doctor-day__hero--now' : ''}`}
        data-testid={`doctor-hero-${kind}`}
      >
        <div className="doctor-day__hero-cap">
          <span className="doctor-day__hero-eyebrow">
            {kind === 'now' ? 'In the chair now' : 'Next in'}
          </span>
          <span className="doctor-day__grow" />
          {kind === 'now' ? (
            <AcBadge tone="brand">{chairChip}</AcBadge>
          ) : waiting !== null ? (
            <AcBadge tone="pending">{`Waiting ${formatDurationHm(waiting)}`}</AcBadge>
          ) : (
            <AcBadge tone={dayStatusChip(row.status).tone}>
              {dayStatusChip(row.status).label}
            </AcBadge>
          )}
        </div>
        <div className="doctor-day__hero-patient">
          <span
            className={`doctor-day__avatar${kind === 'now' ? ' doctor-day__avatar--now' : ''}`}
            aria-hidden="true"
          >
            {nameInitials(name)}
          </span>
          <div>
            <AcButton
              variant="ghost"
              size="sm"
              className="doctor-day__hero-name"
              onClick={() => setDrawer({ appointmentId: row.id, seedTitle: name })}
              data-testid={`doctor-day-open-${row.id}`}
            >
              {name}
            </AcButton>
            <div className="doctor-day__hero-sub">
              {/* DOCTOR-DAY-VISIT-HISTORY-BADGE-V1: both heroes carry the
                  badge on the same terms as any other row -- there is no
                  product reason for the next patient to be less legible
                  than the current one. THE ONE doctor-side expression
                  (doctorVisitHistoryBadge, shared with every worklist row);
                  honest absence when unproven. */}
              {doctorVisitHistoryBadge(row)}
              {umr !== null ? (
                <span className="doctor-day__row-umr">{umr}</span>
              ) : null}
              {subSegments.map((seg, i) => (
                <span
                  key={seg}
                  {...(kind === 'now' && i === subSegments.length - 1 && chair?.courseLine === seg
                    ? { 'data-testid': 'doctor-hero-course' }
                    : {})}
                >
                  {umr !== null || i > 0 ? `${MID_DOT} ${seg}` : seg}
                </span>
              ))}
            </div>
          </div>
        </div>
        {/* A20/A21 (DOCTOR-HOME-PARITY-V1): the next card's four labelled facts.
            PLACEMENT FOLLOWS THE FRAME REGION, not convenience. In
            home-normal.html the next card is a VERTICAL stack -- label (:199)
            -> name (:200) -> meta (:201) -> `.facts` (:203-208) -> the
            `Start consult` button (:210). The first build appended this block
            INSIDE the action row, so the facts, the button and the queue tail
            competed across one line and the button wrapped mid-word. It now
            sits where the frame puts it: after the patient line, before the
            action. Each fact drops out when the record does not serve it --
            the card shows what is known, never a placeholder -- and an
            unanswered allergy renders NO row rather than a reassurance the
            record never gave. */}
          {kind === 'next' && nextFacts?.appointmentId === row.id
            ? nextFacts.facts.length > 0 && (
                <dl className="doctor-day__facts" data-testid="doctor-hero-facts">
                  {nextFacts.facts.map((fact) => (
                    <div className="doctor-day__fact" key={fact.key}>
                      <dt className="doctor-day__fact-label">{fact.label}</dt>
                      <dd
                        className="doctor-day__fact-value"
                        data-testid={`doctor-hero-fact-${fact.key}`}
                      >
                        {fact.tone !== undefined ? (
                          <AcBadge tone={fact.tone}>{fact.value}</AcBadge>
                        ) : (
                          fact.value
                        )}
                        {fact.extra != null ? (
                          <AcBadge tone="pending">{fact.extra}</AcBadge>
                        ) : null}
                      </dd>
                    </div>
                  ))}
                </dl>
              )
            : null}
        <div className="doctor-day__hero-act">
          {doctorConsultDoor(row, 'hero')}
          <span className="doctor-day__grow" />
          {/* D21: the owed-work meta (the founder TASK/PENDING detail) --
              what this chair patient still OWES to finish, from the same
              consult-context read. */}
          {kind === 'now' && chair?.appointmentId === row.id && chair.owedLine !== null ? (
            <span className="doctor-day__hero-meta" data-testid="doctor-hero-owed">
              {chair.owedLine}
            </span>
          ) : null}

          {/* D30: the queue tail (mockup grammar: "then <first name>
              <mid-dot> <n> more present"). */}
          {kind === 'next'
            ? (() => {
                const tail = queueTailLabel(
                  startableWaiting
                    .filter((r) => r.id !== row.id)
                    .map((r) => dayBoardPatientLabel(r)),
                );
                return tail !== null ? (
                  <span className="doctor-day__hero-meta" data-testid="doctor-hero-then">
                    {tail}
                  </span>
                ) : null;
              })()
            : null}
        </div>
      </section>
    );
  };

  // "present" nor "expected", and claiming either filter would re-file it.

  return (
    <div className="doctor-portal doctor-day">
      <CashCheckInbox clinicId={globalPick} />
      {/* THE COCKPIT HEADER: "My day" + the ONE DateCommandBar + the quiet
          metrics ribbon. LABEL DEDUP holds: role + clinic stay in the app
          chrome, never re-stated here.

          CROSS-ROLE-HOME-GRAMMAR-V1 (founder ruling D2): the masthead is the
          REGISTERED page header now -- the same component the front desk and
          COC homes open with -- and the day control rides its registered
          date slot. D2 is explicit that the doctor KEEPS "My day": the
          receptionist operates a clinic, the doctor operates their own
          working day, so the component grammar converges while the copy
          stays role-appropriate. No eyebrow is passed (DR-19 LABEL DEDUP --
          the role is never duplicated on the page), which is exactly the
          role-shaping the shared header is built to allow. */}
      <div className="doctor-day__masthead">
        <PageHeader
          title="My day"
          subs={doctorUsesClinicBoard ? ['Clinic queue'] : !doctorQueueEnabled && boardDoctor.kind !== 'absent' ? [boardDoctor.label] : []}
          actions={doctorQueueEnabled ? (
            <div className="doctor-day__queue-choice">
              <SegmentedControl label="Patient view" testId="doctor-patient-view"
                options={[
                  { key: 'my', label: 'My patients', pressed: selectedView === 'my', testId: 'doctor-view-my' },
                  { key: 'clinic', label: 'Clinic queue', pressed: selectedView === 'clinic', testId: 'doctor-view-clinic' },
                ]}
                onSelect={key => setQueueView({ clinicId: globalPick, value: key === 'clinic' ? 'clinic' : 'my' })}
              />
              <span className="doctor-day__doctor" data-testid="doctor-day-doctor">
                {selectedView === 'clinic' ? 'Clinic queue' : identity.status === 'available' ? identity.doctorName : 'Doctor mapping unavailable'}
              </span>
            </div>
          ) : undefined}
          scope={
            /* A4 (DOCTOR-HOME-PARITY-V1): the approved frame carries
               "<clinic> - <time> IST" in the header (home-normal.html:141).
               SAY IT ONCE: the clinic is already carried here by the shared
               clinic-scope primitive, so the header adds only the MISSING half
               -- the clinic's local time -- beside it. With no scope label the
               frame's full line composes instead. */
            clinicCtx !== null && clinicCtx.selectedLabel !== '' ? (
              <>
                <ClinicScopeLabel
                  label={clinicCtx.selectedLabel}
                  testId="doctor-day-clinic"
                />
                {doctorClockLabel(new Date(nowMs).toISOString()) !== null ? (
                  <span className="doctor-day__clock" data-testid="doctor-day-clock">
                    {doctorClockLabel(new Date(nowMs).toISOString())}
                  </span>
                ) : null}
              </>
            ) : doctorPlaceClockLine(
                boardDoctor.kind === 'clinic-queue' ? boardDoctor.label : null,
                new Date(nowMs).toISOString(),
              ) !== null ? (
              <span className="doctor-day__clock" data-testid="doctor-day-clock">
                {doctorPlaceClockLine(
                  boardDoctor.kind === 'clinic-queue' ? boardDoctor.label : null,
                  new Date(nowMs).toISOString(),
                )}
              </span>
            ) : null
          }
          dateControl={
            <DateCommandBar
              date={day.date}
              today={day.today}
              onSelect={day.selectDate}
              onStep={day.stepDay}
              onToday={day.goToday}
              label="Clinic day"
              variant="compact"
              testId="doctor-day-datebar"
            />
          }
          testId="doctor-day-head"
        />
        {/* THE MASTHEAD STORY -- S1, THE REGISTERED OPENING LINE.
            REF-004 `hercules-doctor-today` (founder-captured; APPROVED
            2026-09-01 for structure and workflow only, never its theme or
            vocabulary): the masthead states the day IN WORDS and then the day's
            shape, on ONE line, before any metric card.

            SHARED PRIMITIVE, NOT A SECOND ONE: `ui/OpeningLine` is the
            registered S1 carrier and the Command Center lane adopted it first,
            unchanged. This surface adopts the SAME component with its own
            clauses -- no doctor-specific opening-line component exists, and
            none should. Clause composition is pure and lives with the other day
            derivations (doctor-home-view: doctorOpeningClauses).

            It costs ONE element: the clauses render joined inside a single
            paragraph, so the story grows in facts without growing in elements. */}
        {openingClauses.length > 0 ? (
          <OpeningLine
            state={doctorOpeningState(owedSummary)}
            clauses={openingClauses}
            testId="doctor-day-opening"
          />
        ) : null}
      </div>

      {/* THE SEVEN-COUNT DAY RIBBON -- INLINE, WHERE IT ALWAYS WAS.
          RESTORED (founder ruling 2026-09-01, preservation-first). A previous
          pass folded these seven counts behind a "SHOW DAY COUNTS" line on the
          argument that each already had a home elsewhere on the screen. The
          founder walked the deployed treatment and ruled it is the one he
          wants: one line under the date nav, zeros light, live figures bold,
          hairline separators. It costs a single line.

          The opening sentence ABOVE it is not a substitute and never was:
          "3 of 6 patients seen" cannot say cancelled, new, returning, waiting
          or in-consult, and on a real day those differ. The sentence says what
          the ribbon cannot (the debt and its age); the ribbon says what the
          sentence cannot. Both stay.

          The fold's own affordance was also the tell -- small grey
          letter-spaced uppercase, no box, no chevron, no underline, on a
          screen where "View all 29 items" carries a disclosure triangle. Two
          disclosure treatments, one of them invisible. It is deleted, not
          restyled: the fold is not wanted.

          D7 still rides underneath: a zero recedes and a count carrying work
          stays loud, decided by the primitive so the rule cannot drift. */}
      <DaySummaryRibbon
        label="My day"
        testId="doctor-my-day"
        /* DOCTOR-RIBBON-FALSE-ZERO-V1: ONE honest line for the absent state,
           in the primitive's own seam slot (the front-desk ribbon's idiom),
           only when the wire says the derivation did not answer. */
        seam={priorVisitUnavailable ? PRIOR_VISIT_COUNTS_SEAM : undefined}
        cells={[
          { key: 'waiting', label: 'Waiting', value: ready ? stats.waiting : '-', quiet: ready ? undefined : true, testId: 'doctor-kpi-waiting' },
          { key: 'in-consult', label: 'In consult', value: ready ? stats.inConsult : '-', quiet: ready ? undefined : true, testId: 'doctor-kpi-in-consult' },
          { key: 'completed', label: 'Completed', value: ready ? stats.completed : '-', quiet: ready ? undefined : true, testId: 'doctor-kpi-completed' },
          { key: 'expected', label: 'Expected', value: ready ? stats.expected : '-', quiet: ready ? undefined : true, testId: 'doctor-kpi-expected' },
          { key: 'cancelled', label: 'Cancelled', value: ready ? stats.cancelled : '-', quiet: ready ? undefined : true, testId: 'doctor-kpi-cancelled' },
          /* DOCTOR-RIBBON-FALSE-ZERO-V1: the two identity cells print the
             count only when the day read landed AND the server's derivation
             answered; otherwise the honest dash, quiet. A genuine 0 stays 0. */
          { key: 'new-patients', label: 'New patients', value: identityCountValue(stats.newPatients, ready, priorVisitUnavailable), quiet: ready && !priorVisitUnavailable ? undefined : true, testId: 'doctor-kpi-new-patients' },
          { key: 'returning-patients', label: 'Returning patients', value: identityCountValue(stats.returningPatients, ready, priorVisitUnavailable), quiet: ready && !priorVisitUnavailable ? undefined : true, testId: 'doctor-kpi-returning-patients' },
        ]}
      />

      {/* THE SEARCH BAR IS BACK AT THE TOP (founder ruling 2026-09-01,
          verbatim: "The search bar is on the top"). A previous pass demoted it
          below the owed-work list on the argument that the doctor's own work
          outranks a lookup box. That was wrong for a reason the frame made
          obvious: search is how a doctor reaches a patient who is NOT on
          today's list, so putting it behind a 29-item backlog puts the
          exceptional case behind the routine one.

          MADE BETTER ONLY WHERE THE APP ALREADY ALLOWS IT. REF-009
          (drwell-owner-dashboard) names what is searchable in the placeholder
          and shows a keyboard hint. The first half we already had --
          "Mobile / UMR / name", not a bare box. The second half is NOT built:
          a repo-wide search for a global search shortcut (cmd/ctrl+K and the
          keydown forms) finds none, and the order forbids building a command
          palette to create one. So the hint is omitted rather than promising
          a shortcut that does not exist, and nothing else about this control
          changed.

          T2 (SHIPPED-WORK-TRUTH-CORRECTIVE-V1, founder ruling 2026-08-15):
          THE GLOBAL SEARCH BAR AT THE TOP -- the SAME door, the SAME
          placement as the front desk's (WalkinApp FO-3: the same hero
          classes, the same submit rule, the same centered modal room).
          Role-aware inside THE ONE modal: the doctor context searches the
          DOCTOR-fenced wire only (contact structurally absent -- stricter
          than masking; fence byte-identical), a picked card opens the
          DOCTOR LENS, and no desk door (register / book / enquiry) exists.
          Lens-gated exactly as the old panel was: no scoped clinic, no
          search (honest absence). The F5 bottom panel is REMOVED -- this
          door replaces it. */}
      {lensEnabled ? (
        <RoleHomeSearchPortal>
        <section className="today-home__hero" aria-label="Find the patient">
          <form
            className="today-home__search"
            onSubmit={(e) => {
              e.preventDefault();
              if (heroQuery.trim().length >= 2) setSearchOpen(true);
            }}
          >
            <input
              className="today-home__search-input"
              type="search"
              value={heroQuery}
              placeholder="Mobile / UMR / name"
              aria-label="Search patients by mobile, UMR, or name"
              autoComplete="off"
              onChange={(e) => setHeroQuery(e.target.value)}
              data-testid="doctor-home-global-search-input"
            />
            {/* Keep search available without competing with the active consult. */}
            <AcButton
              type="submit"
              variant="secondary"
              disabled={heroQuery.trim().length < 2}
              data-testid="doctor-home-global-search-submit"
            >
              Search
            </AcButton>
          </form>
          {/* CROSS-ROLE-HOME-GRAMMAR-V1 (founder ruling D5): the helper
              sentence is GONE. It said "Search by mobile number, UMR (e.g.
              AC-000123), or patient name" beneath a box whose placeholder
              already reads "Mobile / UMR / name" -- the control explains
              itself, so the sentence only repeated it. Helper copy survives
              only where it adds something the control cannot say (the front
              desk keeps "Registering a new patient starts here too", because
              no placeholder can carry that). The constant itself stays where
              it lives: the doctor patient-search page still renders it as
              that screen's own sub line, and no search MODE changed here. */}
        </section>
        </RoleHomeSearchPortal>
      ) : null}

      {/* THE TWO-UP HEROES (today only): the chair and the next patient as
          co-equal command panels -- the ten-second answer. On a planning
          day, or with nobody in the chair/waiting, a panel is ABSENT
          (contextual existence -- never an empty hero shell). */}
      {heroNow !== null || heroNext !== null ? (
        <div className="doctor-day__heroes" data-testid="doctor-day-heroes">
          {heroNow !== null ? (
            heroPanel(heroNow, 'now')
          ) : chairEmptyLine(heroNext !== null ? dayBoardPatientLabel(heroNext) : null) !== null ? (
            /* A16 (DOCTOR-HOME-PARITY-V1): "Chair empty - Meera is next"
               (transition-home.html:181). The chair's absence is a FACT the
               doctor reads, not a gap -- but only when somebody actually is
               next; a line naming nobody would be the empty shell this screen
               refuses elsewhere ("never an empty hero shell"). */
            <section className="doctor-day__hero doctor-day__chair-empty-panel">
              {/* A16 follows the IN CHAIR band region (transition-home.html:181):
                  the band keeps its label and states the chair's condition as a
                  row inside it. Without the cap this printed as a lone sentence
                  floating in an otherwise empty half of the hero grid at 1440. */}
              <div className="doctor-day__hero-cap">
                <span className="doctor-day__hero-eyebrow">In the chair now</span>
              </div>
              <p className="doctor-day__chair-empty" data-testid="doctor-day-chair-empty">
                {chairEmptyLine(dayBoardPatientLabel(heroNext as TodayAppointment))}
              </p>
            </section>
          ) : null}
          {heroNext !== null ? heroPanel(heroNext, 'next') : null}
        </div>
      ) : null}

      {/* HOME HERO ORDER (A1, DOCTOR-OS-EXPERIENCE-V1): the day is the hero
          above; OWED WORK is second -- the home never leads with a filing
          inbox.

          DOCTOR-TODAY-CRAFT-V1: "second" is now TRUE. This law was written
          here and then contradicted by the render, which mounted the rail
          LAST -- below the worklist and below the collapsed Done-today bar,
          sixth of seven regions. A doctor between patients, when both hero
          panels are absent by design, met a row of zeroes at the top and
          found what they actually owed a scroll below the fold. The rail
          moves to the position its own comment always claimed: after the
          chair, before the rest of the day. Nothing about the rail's read,
          fences or doors changed -- only where it sits. CORRECTIVE ROUND 1 (DOCTOR-OWED-WORK-QUEUE-V1, product-level
          AC-3): the owed-work rail mounts REGARDLESS of the attachments
          config -- the doctor's draft work must not sit behind it. Capture
          affordances are absent when attachments are dark (the read returns
          zero evidence items -> no Capture rows); draft rows + Resume still
          render, and the S-4 empty line shows when nothing is owed. */}
      {/* THREE ROWS ON THE DAY BOARD, NOT FIVE (founder ruling 2026-09-01).
          The requirement is "do not bury 29 owed items", NOT "make debt occupy
          half the workspace". Three rows plus "View all 29 items" keeps the
          urgency and the oldest-first ordering while leaving the day's own
          schedule the room it needs. Chosen off the rendered frame: at five,
          the owed card ran to the fold and pushed the day list off-screen in
          the between-patients state; at three it ends well above it in both
          states. Nothing is unreachable -- the fold holds the other 26. */}
      <DoctorInboxRail onSummary={setOwedSummary} rowCap={DAY_BOARD_OWED_ROW_CAP} />

      {/* THE REST OF THE DAY (DOCTOR-TODAY-CRAFT-V1 corrective).

          THE DEFECT, MEASURED ON THE SHIPPED FRAME: three patients who had
          NOT ARRIVED spent 25 of 56 visible elements -- 45% of the screen,
          more than the dominant job. A doctor's Today is for who is in the
          chair and who is next; a not-arrived patient is a waiting-room
          fact, not the doctor's foreground.

          THE FIX IS SUBTRACTION, NOT A NEW CARD. Nothing was added above
          this. The EXPECTED band leaves the table and becomes ONE collapsed
          line -- the SAME disclosure idiom the Done-today bar on this very
          screen already uses (count badge + names + Show), so no new
          vocabulary is minted. The table keeps only the bands the doctor can
          act on now: Present, and desk-closed Draft open.

          THE THREE FILTER TABS ARE CONSOLIDATED INTO THAT DISCLOSURE, not
          deleted. All / Present / Expected existed to show one band at a
          time above groups that were already labelled and counted -- the
          same question answered twice. With Expected folded, "Present" IS
          the default view and "Expected" IS the fold, so the capability
          survives in one control instead of three. Nothing a doctor could
          reach before is unreachable now. */}
      <section className="doctor-day__list" data-testid="doctor-queue-section">
        <div className="doctor-day__list-head">
          <span className="doctor-day__list-title">The rest of the day</span>
          <span className="doctor-day__list-count">{ready ? restCount : '-'}</span>
          <span className="doctor-day__grow" />
          {/* THE THREE FILTER TABS ARE BACK AND STAY VISIBLE (founder ruling
              2026-09-01, twice now: a visible control on this screen is not
              replaced by an interaction). A previous pass folded the Expected
              band into a disclosure and argued the tabs had been "consolidated"
              into it. They had not -- three controls became one, and the doctor
              lost the ability to isolate Present or Expected in place. */}
          <div className="doctor-day__tabs" role="tablist" aria-label="Worklist filter">
            {(
              [
                { key: 'all', label: 'All' },
                { key: 'present', label: 'Present' },
                { key: 'expected', label: 'Expected' },
              ] as const
            ).map((t) => (
              <AcButton
                key={t.key}
                variant="ghost"
                size="sm"
                role="tab"
                aria-selected={tab === t.key}
                className={`doctor-day__tab${tab === t.key ? ' doctor-day__tab--on' : ''}`}
                onClick={() => setTab(t.key)}
                data-testid={`doctor-day-tab-${t.key}`}
              >
                {t.label}
              </AcButton>
            ))}
          </div>
        </div>

        {queue.kind === 'loading' ? (
          <p className="doctor-day__empty">Loading the day...</p>
        ) : queue.kind === 'error' ? (
          <div data-testid="doctor-day-error">
            <DashboardLoadError message="The day board could not load right now." />
          </div>
        ) : identityUnavailable ? (
          <p className="doctor-day__empty" role="status" data-testid="doctor-mapping-unavailable">
            {identity.status === 'conflict' ? 'Your doctor mapping is ambiguous for this clinic.' : 'Your doctor mapping is unavailable for this clinic.'}
          </p>
        ) : restCount === 0 ? (
          <p className="doctor-day__empty" data-testid="doctor-queue-all-done">
            {active.length === 0 && done.length > 0
              ? "Everyone seen - today's visits are in Done today below."
              : heroIds.size > 0
                ? 'Nothing else scheduled - the day is in the two panels above.'
                : 'No appointments on this day.'}
          </p>
        ) : (
          /* D9: ONE table, the bands GROUPED INSIDE IT -- Present, Draft open
             AND Expected, all three restored to the table. The primitive ranks
             nothing: the groups arrive in the order this surface already put
             them in. */
          <DataTable
            testId="doctor-queue-bands"
            label="The rest of the day"
            columns={worklistColumns}
            rows={[]}
            rowKey={(row) => row.id}
            rowTestId={(row) => `doctor-day-row-${row.id}`}
            empty={<p className="doctor-day__empty">Nothing under this filter.</p>}
            groups={[
              {
                key: 'present',
                testId: 'doctor-band-present',
                label: 'Present',
                /* Mockup grammar: "Present 1 more" when a hero absorbed the
                   first present row; the plain count otherwise. */
                meta:
                  heroNext !== null
                    ? `${filteredPresent.length} more`
                    : filteredPresent.length,
                rows: filteredPresent,
              },
              {
                key: 'draft-open',
                testId: 'doctor-band-draft-open',
                /* W-5 (corrective): desk-closed open paperwork -- the owner's
                   own group, after Present. */
                label: 'Draft open',
                meta: filteredDraftOpen.length,
                rows: filteredDraftOpen,
              },
              {
                key: 'expected',
                testId: 'doctor-band-expected',
                label: 'Expected',
                meta: filteredExpected.length,
                rows: filteredExpected,
              },
            ]}
          />
        )}

        {/* THE DONE BAR: outcome chips + names + the Show disclosure (the
            SAME rows the collapsed board carried; count always visible). */}
        {done.length > 0 ? (
          <details className="doctor-day__done" data-testid="doctor-done-group">
            <summary className="doctor-day__done-summary">
              <span className="doctor-day__done-label">Done today</span>
              {doneComplete.length > 0 ? (
                <AcBadge tone="positive">{`${doneComplete.length} visit complete`}</AcBadge>
              ) : null}
              {doneNoShow.length > 0 ? (
                <AcBadge tone="neutral">{`${doneNoShow.length} no-show`}</AcBadge>
              ) : null}
              {doneCancelled.length > 0 ? (
                <AcBadge tone="neutral">{`${doneCancelled.length} cancelled`}</AcBadge>
              ) : null}
              <span className="doctor-day__done-names">{doneNames}</span>
              <span className="doctor-day__grow" />
              <span className="doctor-day__done-show">Show</span>
            </summary>
            {/* D9: the disclosed rows are the SAME table shape as the
                worklist above -- same columns, same order, same cells -- so
                opening the bar never changes what the eye is scanning. */}
            <DataTable
              testId="doctor-done-rows"
              label="Done today"
              columns={worklistColumns}
              rows={done}
              rowKey={(row) => row.id}
              rowTestId={(row) => `doctor-day-row-${row.id}`}
            />
          </details>
        ) : null}
      </section>

      {/* T2 (founder ruling 2026-08-15): the F5 bottom search panel is
          REMOVED -- the search door now lives at the TOP of this page (the
          front-desk placement), opening THE ONE GlobalPatientSearchModal in
          its doctor context. The doctor-fenced wire and its fences are
          byte-untouched; only the door moved. */}


      {/* B2-v2 (FOUNDER OVERRIDE 2026-08-01): the shift + incentive card no
          longer renders on Doctor Today or any patient-care surface -- the
          doctor clinical plane carries NO incentive, target, or
          monetary-performance field (NEVER-SHOW row 6, structural pin). The
          capability is PRESERVED UNRENDERED in DoctorShiftIncentiveCard.tsx +
          api/routes/doctor.ts computeDoctorIncentiveSummary; its future home
          is an open founder decision (run record, Rajeshwari question). */}

      {/* ONE HOME PER FACT (DOCTOR-PRECALL-POLISH-V1, DR-21): the Recent
          patients panel stays retired; the "All patients" header door owns
          the route (LES-016 -- never a dead end). The doctor-fenced
          fetchDoctorRecentPatients + DoctorPatientRows stay preserved
          unrendered. */}

      {/* THE WHATSAPP QUICK-COMMANDS NOTE IS REMOVED (DOCTOR-TODAY-CRAFT-V1,
          founder count ruling 2026-09-01: "delete anything explaining a
          DIFFERENT screen"). It was a help card for the WhatsApp CHANNEL --
          shift:confirm, shift:delay, doctor:count -- sitting at the foot of
          the doctor's clinical day board. None of it answers "who is next,
          and what do I owe", none of it is a control on this surface, and
          the channel it documents is the administrative plane this screen is
          fenced from by the Two-Channel Rule (ADR-0019).

          NOT A CAPABILITY LOSS: the commands are handled by the WhatsApp
          webhook, not by this page -- a doctor who types shift:confirm into
          WhatsApp gets the same answer as before. What is gone is a static
          list of them on a screen that is not WhatsApp. The safety sentence
          it carried ("never send patient names or clinical details on
          WhatsApp") is the Two-Channel Rule, whose home is ADR-0019 and the
          channel itself, not a footnote here. This is the one deletion in
          this package a founder might want back; it is a single block to
          restore if so. */}

      {/* The doctor-safe context drawer (DOCTOR-CONTEXT LAW): one drawer,
          keyed on the appointment id, opened from any row/hero name. */}
      {drawer !== null ? (
        <DoctorQueueContextDrawer
          appointmentId={drawer.appointmentId}
          seedTitle={drawer.seedTitle}
          onClose={() => setDrawer(null)}
        />
      ) : null}

      {/* T2: the centered search modal -- THE ONE search room (REUSE-THE-
          DOOR), in its doctor context. The typed query survives the modal
          closing (it lives on this page), the FO-3 state shape. */}
      {searchOpen ? (
        <GlobalPatientSearchModal
          initialQuery={heroQuery}
          onClose={() => {
            setSearchOpen(false);
            requestAnimationFrame(() => document.querySelector<HTMLElement>('[data-testid="doctor-home-global-search-input"]')?.focus());
          }}
          audience="doctor"
        />
      ) : null}
    </div>
  );
}
